import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Empty, Input, List, Modal, Space, Spin, Tag, Typography, message as antdMessage } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import * as api from "api/communication";

const { Text } = Typography;

/**
 * Link a website-chat visitor to a CRM account by hand.
 *
 * Visitors are anonymous, so the rep asks for something only the real
 * customer would know — registered email or phone, GSTIN, account number —
 * searches on it, and checks the details shown against what the visitor says
 * before linking. Each result says which field it matched on.
 */

const SEARCH_DELAY_MS = 300;

const Detail = ({ label, value }) =>
  value ? (
    <span style={{ marginRight: 12, whiteSpace: "nowrap" }}>
      <Text type="secondary">{label}:</Text> {value}
    </span>
  ) : null;

const LinkAccountModal = ({ open, conversationRef, linkedAccountId, onClose, onLinked }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const latest = useRef(0);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const ticket = ++latest.current;
    const timer = setTimeout(async () => {
      try {
        const response = await api.searchAccounts(conversationRef, q);
        // Ignore answers to searches the rep has already typed past.
        if (ticket === latest.current) setResults(response.data || []);
      } catch (error) {
        if (ticket === latest.current) setResults([]);
      }
      if (ticket === latest.current) setSearching(false);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query, open, conversationRef]);

  const link = async (accountId) => {
    setSavingId(accountId || "unlink");
    try {
      await api.linkAccount(conversationRef, accountId);
      antdMessage.success(accountId ? "Account linked to this chat" : "Account unlinked");
      onLinked();
      onClose();
    } catch (error) {
      antdMessage.error(error?.response?.data?.error || "Could not update the account");
    }
    setSavingId(null);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Link an account to this chat"
      width={640}
      footer={
        linkedAccountId ? (
          <Button danger loading={savingId === "unlink"} onClick={() => link(null)}>
            Unlink current account
          </Button>
        ) : null
      }
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Confirm who you are talking to"
        description="Ask the visitor for their registered email, phone number, GSTIN or account number, search for it, and check the details below match before linking."
      />
      <Input
        autoFocus
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Email, phone, GSTIN, account number or name"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <div style={{ marginTop: 12, maxHeight: 360, overflowY: "auto" }}>
        {searching ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        ) : query.trim().length < 2 ? null : !results.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No matching accounts" />
        ) : (
          <List
            dataSource={results}
            renderItem={(account) => {
              const isLinked = account.id === linkedAccountId;
              return (
                <List.Item
                  actions={[
                    <Button
                      key="link"
                      type={isLinked ? "default" : "primary"}
                      size="small"
                      disabled={isLinked}
                      loading={savingId === account.id}
                      onClick={() => link(account.id)}
                    >
                      {isLinked ? "Linked" : "Link"}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space size={6} wrap>
                        <span>{account.display_name}</span>
                        {account.account_number ? <Tag>{account.account_number}</Tag> : null}
                        <Tag color="blue">Matched: {account.matched_on}</Tag>
                      </Space>
                    }
                    description={
                      <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                        <Detail label="Contact" value={account.contact_person} />
                        <Detail label="Email" value={account.email} />
                        <Detail label="Phone" value={account.phone || account.wa_number} />
                        <Detail label="GSTIN" value={account.gst_number} />
                        <Detail label="City" value={account.city} />
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </div>
    </Modal>
  );
};

export default LinkAccountModal;
